import { FormAnalysis, Money } from "types/landlord/reports";
import { PayStubI } from "views/applications-id-report/utils/PayStub";

export enum DocType {
  PAY_STUB = "pay_stub",
  W2 = "w2",
  BANK_STATEMENT = "bank_statement"
}

export type PayStubIncomeSource = {
  employer: string;
  start_date: string;
  end_date: string;
  net_monthly_income_amount: number;
  gross_monthly_income_amount: number;
  net_monthly_income: Money;
  gross_monthly_income: {
    cents: number;
    currency: string;
  };
  pay_stubs: PayStubI[];
  income_source_type?: string;
  docType: DocType.PAY_STUB;
};

type BankStatementDocument = {
  pk: number;
  name: string;
  uuid: string;
  pages: number;
  status: string;
  sub_doc_type: unknown;
  rejection_reason: unknown;
  uploaded_doc_type: string;
  sub_doc_type_details: unknown;
  rejection_reason_description: unknown;
  asset?: {
    asset?: {
      url: string;
      content_type: string;
      filename: string;
      size: number;
      extension: string;
    };
    global_id: string;
  };
  doc_analysis: {
    detect_status: string;
    form_analysis: {
      signals: {
        identifier: string;
        display_name: string;
        signal_count: number;
        supporting_data: {
          values: {
            key: string;
            value: string;
            data_type: string;
          }[];
        }[];
      }[];
      form_type: string;
      form_uuid: string;
      visualizations: {
        page_number: number;
        page_visualizations: {
          image_url: string;
          description: string;
          visual_type: string;
          display_name: string;
        }[];
      }[];
      form_authenticity?: {
        score: number;
        version: string;
        reason_codes: {
          code: string;
          confidence: string;
          description: string;
        }[];
      };
      form_dashboard_url: string;
    }[];
    uploaded_doc_type: string;
    uploaded_doc_uuid: string;
    is_image_based_pdf: boolean;
  };
  transactions: {
    pk: number;
    bbox: number[];
    tags: any;
    amount: {
      cents: number;
      currency: string;
    };
    comment: string;
    page_idx: number;
    txn_date: string;
    period_pk: number;
    description: string;
    explanation: string;
    page_doc_pk: number;
    bank_account_pk: number;
    uploaded_doc_pk: number;
    txn_pk_duplicate_of: unknown;
    has_probable_transfers: boolean;
    original_amount: string;
  }[];
};

export type BankStatementPeriod = {
  pk: number;
  uuid: string;
  end_date: string;
  begin_date: string;
  end_balance: Money;
  begin_balance: Money;
  bank_account_pk: number;
  uploaded_doc_pk: number;
  primary_recon_error_reason: unknown;
  secondary_recon_error_reason: unknown;
  original_begin_balance: string;
  original_end_balance: string;
  average_daily_balance: Money;
};

export type BankStatementIncomeSource = {
  id: number;
  pk: number;
  name: string;
  book_pk: number;
  periods?: BankStatementPeriod[];
  bank_name: string;
  book_uuid: string;
  holder_zip: string;
  holder_city: string;
  account_type: string;
  holder_state: string;
  activity_info: {
    active: {
      end: {
        day: number;
        year: number;
        month: number;
      };
      start: {
        day: number;
        year: number;
        month: number;
      };
    }[];
    missing: any[];
  };
  account_holder: string;
  account_number: string;
  holder_country: string;
  account_holders: [string];
  account_category: string;
  holder_address_1: string;
  holder_address_2?: string;
  net_monthly_income_amount: number;
  gross_monthly_income_amount: number;
  net_monthly_income: Money;
  gross_monthly_income: Money;
  uploaded_docs: BankStatementDocument[];
  income_source_type?: string;
  docType?: DocType.BANK_STATEMENT;
};

interface FieldData<T> {
  value: T;
  is_empty: boolean;
  alias_used: string | null;
  confidence: number;
  source_filename: string;
}

export interface W2RawFields {
  box9?: FieldData<string>;
  year?: FieldData<number>;
  box12aCode?: FieldData<string>;
  box12bCode?: FieldData<string>;
  box12cCode?: FieldData<string>;
  box12dcode?: FieldData<string>;
  box14Other?: FieldData<string>;
  box12aAmount?: FieldData<Money>;
  box12bAmount?: FieldData<Money>;
  box12cAmount?: FieldData<Money>;
  box12dAmount?: FieldData<Money>;
  employeeName?: FieldData<string>;
  employerIdNo?: FieldData<string>;
  employerName?: FieldData<string>;
  statePrimary?: FieldData<string>;
  allocatedTips?: FieldData<Money>;
  qualifiedPlans?: FieldData<Money>;
  retirementPlan?: FieldData<string>;
  stateSecondary?: FieldData<string>;
  statutoryEmployee?: FieldData<string>;
  employeeAddressZip: FieldData<number>;
  employerAddressZip?: FieldData<number>;
  socialSecurityTips?: FieldData<Money>;
  "third-PartySickPay"?: FieldData<string>;
  wagesTipsOtherComp?: FieldData<Money>;
  employeeAddressCity?: FieldData<string>;
  employerAddressCity?: FieldData<string>;
  localityNamePrimary?: FieldData<string>;
  medicareTaxWithheld?: FieldData<Money>;
  socialSecurityWages?: FieldData<Money>;
  employeeAddressLine1?: FieldData<string>;
  employeeAddressLine2?: FieldData<string>;
  employeeAddressState?: FieldData<string>;
  employerAddressLine1?: FieldData<string>;
  employerAddressLine2?: FieldData<string>;
  employerAddressState?: FieldData<string>;
  medicareWagesAndTips?: FieldData<Money>;
  dependentCareBenefits?: FieldData<Money>;
  localIncomeTaxPrimary: FieldData<string>;
  localWagesTipsPrimary?: FieldData<Money>;
  localityNameSecondary?: FieldData<string>;
  stateIncomeTaxPrimary?: FieldData<Money>;
  stateWagesTipsPrimary?: FieldData<Money>;
  localIncomeTaxSecondary: FieldData<string>;
  localWagesTipsSecondary?: FieldData<string>;
  stateIncomeTaxSecondary?: FieldData<Money>;
  stateWagesTipsSecondary?: FieldData<Money>;
  federalIncomeTaxWithheld?: FieldData<Money>;
  socialSecurityTaxWithheld: FieldData<Money>;
  employeeSocialSecurityNumber?: FieldData<string>;
  employerStateIdNumberPrimary?: FieldData<string>;
  employerStateIdNumberSecondary: FieldData<string>;
}

export type W2Form = {
  annual_gross_income_amount: number;
  annual_net_income_amount: number;
  asset?: {
    asset?: {
      url: string;
      content_type: string;
      filename: string;
      size: number;
      extension: string;
    };
    global_id: string;
  };
  attribute_data: unknown;
  doc_analysis: {
    detect_status: string;
    form_analysis: FormAnalysis[];
    uploaded_doc_type: string;
    uploaded_doc_uuid: string;
    is_image_based_pdf: boolean;
  };
  end_date: string;
  federal_income_tax_withheld: number;
  fields: unknown;
  form_config_pk: number;
  form_type: string;
  gross_monthly_income: {
    cents: number;
    currency: string;
  };
  gross_monthly_income_amount: number;
  local_income_tax_primary: number;
  medicate_tax_withheld: number;
  net_monthly_income: {
    cents: number;
    currency: string;
  };
  net_monthly_income_amount: number;
  pk: number;
  raw_fields: W2RawFields;
  social_security_tax_withheld: number;
  start_date: string;
  state_income_tax_primary: number;
  tables: unknown[];
  total_taxes: number;
  uploaded_doc_pk: number;
  uuid: string;
  year: string;
};

export type W2IncomeSources = {
  employer_name: string;
  end_date: string;
  forms: W2Form[];
  gross_monthly_income: Money;
  gross_monthly_income_amount: number;
  net_monthly_income: Money;
  net_monthly_income_amount: number;
  start_date: string;
  income_source_type?: string;
  docType?: DocType.W2;
};
